import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MessageSquare, Calendar, ArrowRight } from 'lucide-react';
import './Contact.css';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Handle form submission logic here
    alert('Thank you for your interest! We\'ll be in touch soon.');
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <motion.div 
          className="contact-header"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">Get in Touch</h2>
          <p className="section-subtitle">
            Ready to revolutionize your biomedical imaging analysis? 
            Let's discuss how our multi-agent system can transform your research workflow.
          </p>
        </motion.div>

        <div className="contact-content">
          <motion.div 
            className="contact-info"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="contact-info-title">Ready to Transform Your Research?</h3>
            
            <div className="contact-items">
              <div className="contact-item">
                <div className="contact-icon">
                  <Calendar className="icon" />
                </div>
                <div className="contact-details">
                  <h4>Request a Demo</h4>
                  <p>Schedule a personalized demonstration of our platform</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <MessageSquare className="icon" />
                </div>
                <div className="contact-details">
                  <h4>Technical Discussion</h4>
                  <p>Deep dive into our technology and integration possibilities</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <Mail className="icon" />
                </div>
                <div className="contact-details">
                  <h4>Email Us Directly</h4>
                  <p>contact.spatiocytic@gmail.com</p>
                </div>
              </div>
            </div>

            <div className="timeline-note">
              <p>
                <strong>Development Timeline:</strong> We deliver the best AI-powered specialized imaging analysis software for you research.
              </p>
            </div>
          </motion.div>

          <motion.form 
            className="contact-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div className="form-group">
              <label htmlFor="name" className="form-label">Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder="Your full name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder="your.email@institution.edu"
              />
            </div>

            <div className="form-group">
              <label htmlFor="organization" className="form-label">Organization</label>
              <input
                type="text"
                id="organization"
                name="organization"
                value={formData.organization}
                onChange={handleInputChange}
                className="form-input"
                placeholder="University, Hospital, or Research Institute"
              />
            </div>

            <div className="form-group">
              <label htmlFor="message" className="form-label">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows={5}
                className="form-textarea"
                placeholder="Tell us about your imaging analysis challenges and how we can help..."
              />
            </div>

            <motion.button
              type="submit"
              className="submit-button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Send Message
              <ArrowRight className="button-icon" />
            </motion.button>
          </motion.form>
        </div>

        <motion.footer 
          className="site-footer"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="footer-content">
            <div className="footer-logo">
              <span className="logo-text">Spatiocytic</span>
            </div>
            <p className="footer-tagline">
              Automating the future of biomedical imaging analysis
            </p>
            <div className="footer-info">
              <span>contact.spatiocytic@gmail.com</span>
              <span>•</span>
              <span>Boston, USA</span>
              <span>•</span>
              <span>B2B SaaS Platform</span>
            </div>
          </div>
        </motion.footer>
      </div>
    </section>
  );
};

export default Contact;